import { Component, ElementRef, Input, OnChanges, ViewChild } from '@angular/core';

interface Frase {
    segundos: number;
    quien: 'Agente' | 'Cliente' | null;
    texto: string;
}

/**
 * Reproductor de una llamada grabada, con su transcripción sincronizada.
 *
 * La transcripción viene con marcas de tiempo por frase, así que se puede
 * navegar el audio LEYENDO en vez de arrastrando una barra a ciegas: pulsar una
 * frase salta a ese momento, y la que suena se resalta sola. En una llamada de
 * ocho minutos, buscar "dónde dijo el precio" pasa de rebobinar a repasar.
 */
@Component({
    selector: 'app-call-player',
    standalone: false,
    templateUrl: './call-player.component.html',
    styleUrls: ['./call-player.component.css'],
})
export class CallPlayerComponent implements OnChanges {
    @ViewChild('audio') audioRef!: ElementRef<HTMLAudioElement>;
    @ViewChild('lista') listaRef?: ElementRef<HTMLElement>;

    @Input() url: string | null = null;
    @Input() transcripcion: string | null = null;

    frases: Frase[] = [];
    reproduciendo = false;
    actual = 0;
    duracion = 0;
    velocidad = 1;
    velocidades = [1, 1.5, 2];
    copiado = false;

    private ultimaActiva = -1;
    private avisoCopiado: any = null;

    ngOnChanges(): void {
        this.frases = this.parsear(this.transcripcion);
        this.ultimaActiva = -1;
    }

    /** Porcentaje reproducido, para pintar la parte ya escuchada de la barra. */
    get avance(): number {
        return this.duracion > 0 ? (this.actual / this.duracion) * 100 : 0;
    }

    /** ¿La transcripción distingue quién habla? Decide si la nota de canales sobra. */
    get hayHablantes(): boolean {
        return this.frases.some((f) => !!f.quien);
    }

    /**
     * Convierte "[0:03] Agente: texto" en frases navegables.
     *
     * Si la transcripción no trae marcas —el caso sin ffmpeg, donde se
     * transcribe la mezcla— se devuelve como un bloque único: sigue leyéndose,
     * solo que sin poder saltar.
     */
    private parsear(texto: string | null): Frase[] {
        if (!texto?.trim()) return [];

        const lineas = texto.split('\n').filter((l) => l.trim());
        const patron = /^\[(\d+):(\d{2})\]\s*(Agente|Cliente)?:?\s*(.*)$/;
        const frases: Frase[] = [];

        for (const linea of lineas) {
            const m = patron.exec(linea.trim());
            if (m) {
                frases.push({
                    segundos: Number(m[1]) * 60 + Number(m[2]),
                    quien: (m[3] as any) ?? null,
                    texto: m[4],
                });
            } else {
                frases.push({ segundos: -1, quien: null, texto: linea.trim() });
            }
        }
        return frases;
    }

    // ── Controles ───────────────────────────────────────────────────────────

    alternar(): void {
        const a = this.audioRef?.nativeElement;
        if (!a) return;
        if (a.paused) void a.play().catch(() => undefined);
        else a.pause();
    }

    /** Salta al momento de una frase. El corazón de la navegación por texto. */
    irA(segundos: number): void {
        const a = this.audioRef?.nativeElement;
        if (!a || segundos < 0) return;
        a.currentTime = segundos;
        if (a.paused) void a.play().catch(() => undefined);
    }

    /** Adelanta o retrocede. Diez segundos es el salto que cubre "no te oí". */
    saltar(segundos: number): void {
        const a = this.audioRef?.nativeElement;
        if (!a) return;
        a.currentTime = Math.min(Math.max(0, a.currentTime + segundos), this.duracion || a.duration || 0);
    }

    buscar(ev: Event): void {
        const a = this.audioRef?.nativeElement;
        if (!a) return;
        a.currentTime = Number((ev.target as HTMLInputElement).value);
    }

    cambiarVelocidad(): void {
        const i = this.velocidades.indexOf(this.velocidad);
        this.velocidad = this.velocidades[(i + 1) % this.velocidades.length];
        const a = this.audioRef?.nativeElement;
        if (a) a.playbackRate = this.velocidad;
    }

    // ── Eventos del <audio> ─────────────────────────────────────────────────

    alCargar(): void {
        const a = this.audioRef?.nativeElement;
        // Las grabaciones vienen de MediaRecorder en trozos y a veces llegan sin
        // duración declarada; `Infinity` rompería la barra de progreso.
        this.duracion = a && Number.isFinite(a.duration) ? a.duration : 0;
    }

    alAvanzar(): void {
        this.actual = this.audioRef?.nativeElement?.currentTime ?? 0;

        const i = this.activa;
        if (i !== this.ultimaActiva) {
            this.ultimaActiva = i;
            this.seguir(i);
        }
    }

    /**
     * Arrastra la lista para que la frase que suena quede a la vista.
     *
     * Sin esto, en una llamada larga el resaltado se va por debajo del recuadro
     * a los pocos segundos y hay que perseguirlo con la rueda del ratón. Solo
     * se mueve si la frase NO se ve: así no pelea con quien está leyendo otra
     * parte mientras escucha.
     */
    private seguir(i: number): void {
        if (i < 0 || !this.reproduciendo) return;

        const lista = this.listaRef?.nativeElement;
        const frase = lista?.querySelector<HTMLElement>(`[data-idx="${i}"]`);
        if (!lista || !frase) return;

        const yaSeVe = frase.offsetTop >= lista.scrollTop
            && frase.offsetTop + frase.offsetHeight <= lista.scrollTop + lista.clientHeight;
        if (yaSeVe) return;

        lista.scrollTop = frase.offsetTop - (lista.clientHeight - frase.offsetHeight) / 2;
    }

    /**
     * Copia la transcripción entera al portapapeles.
     *
     * Es lo que se hace con ella en la práctica: pegarla en la nota del
     * registro de Zoho o pasársela a un compañero. Seleccionarla a mano dentro
     * de un recuadro con scroll es incómodo y se corta a media frase.
     */
    async copiar(): Promise<void> {
        const texto = this.frases
            .map((f) => (f.quien ? `${f.quien}: ${f.texto}` : f.texto))
            .join("\n");
        if (!texto) return;

        try {
            await navigator.clipboard.writeText(texto);
            this.copiado = true;
            clearTimeout(this.avisoCopiado);
            this.avisoCopiado = setTimeout(() => (this.copiado = false), 2000);
        } catch {
            // Sin permiso de portapapeles no hay nada que hacer: el texto sigue
            // en pantalla y se puede seleccionar a mano.
        }
    }

    /** Índice de la frase que suena ahora, para resaltarla. */
    get activa(): number {
        let idx = -1;
        for (let i = 0; i < this.frases.length; i++) {
            if (this.frases[i].segundos >= 0 && this.frases[i].segundos <= this.actual) idx = i;
            else if (this.frases[i].segundos > this.actual) break;
        }
        return idx;
    }

    reloj(segundos: number): string {
        if (!Number.isFinite(segundos) || segundos < 0) return '0:00';
        const m = Math.floor(segundos / 60);
        const s = Math.floor(segundos % 60);
        return `${m}:${String(s).padStart(2, '0')}`;
    }
}
